import { useState, useEffect, useCallback, useRef, Component } from 'react';
import { getItems, createItem, updateItem, STROKES, YARDS_TO_METERS } from './fitnessApi';
import { StopwatchDisplay, RestClocksDisplay, formatStopwatch } from './workoutTiming';
import defineholyLogo from '../assets/defineholylogo.svg';

const TEMPLATES_LIST = 'SwimmingTemplates';
const WORKOUTS_LIST = 'SwimmingWorkouts';
const PERSIST_KEY = 'dh_swimWorkoutInProgress';
// Exported: SwimmingHistory uses the same window to decide which workouts are
// still editable — one recency definition, not two.
export const RESUME_TTL_MS = 3 * 60 * 60 * 1000;

// A stop tap under this many seconds is an accidental double-tap, not a rep.
// The June phantom reps were 0.4s and 0.6s; the shortest legitimate rep ever
// recorded is 54.1s (50 Fly), so 5s rejects the accident class with ~49s of
// margin. Applies ONLY to the stop tap — Done mid-rep is a deliberate act and
// captures whatever the clock reads.
const MIN_REP_SECONDS = 5;

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

// StopwatchDisplay and RestClocksDisplay live in workoutTiming.js now, shared
// with the lifting runner. Extracted verbatim; behavior here is unchanged.

// ── SwimmingWorkout ───────────────────────────────────────────────────────────

function SwimmingWorkout({ getToken, userEmail }) {
  // Pool settings (persisted in localStorage)
  const [poolLength, setPoolLength] = useState(() => {
    return Number(localStorage.getItem('dh_poolLength')) || 25;
  });
  const [poolUnit, setPoolUnit] = useState(() => {
    return localStorage.getItem('dh_poolUnit') || 'm';
  });
  const [displayUnit, setDisplayUnit] = useState(() => {
    return localStorage.getItem('dh_displayUnit') || 'm';
  });

  // Template selection
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState(null);

  // Workout state
  const [workout, setWorkout] = useState(null); // { templateName, sets, startTime }
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedSets, setCompletedSets] = useState([]);
  const [editingCurrent, setEditingCurrent] = useState(null); // edit-before-confirm
  const [showAddSet, setShowAddSet] = useState(false);
  const [newSet, setNewSet] = useState(null);
  const [poolsideMode, setPoolsideMode] = useState(true);
  const [restTimer, setRestTimer] = useState(null); // seconds remaining
  const [showSummary, setShowSummary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workoutStartClock, setWorkoutStartClock] = useState(null); // ISO string — first stopwatch tap

  // Stopwatch state (per-rep timing in poolside mode)
  const [currentRep, setCurrentRep] = useState(1);
  const [repTimes, setRepTimes] = useState([]);
  const [stopwatchRunning, setStopwatchRunning] = useState(false);
  const [stopwatchStartTime, setStopwatchStartTime] = useState(null);
  const [frozenStopwatchTime, setFrozenStopwatchTime] = useState(null); // shown after STOP, cleared on START/reset

  // Actual rest, measured wall-clock. repRests[i] is the rest taken BEFORE repTimes[i],
  // so the two arrays always have equal length. repRests[0] of a set is the inter-set
  // rest — lastRepStopRef deliberately survives set boundaries and is cleared only by
  // startWorkout. The workout's very first rep has no prior stop, so it records null.
  const [repRests, setRepRests] = useState([]);
  // Parallel to repRests, same length: the target actually in force while that rest
  // was being taken — setTarget when the clock was in 'set' mode, else repTarget.
  // null wherever repRests is null. Distinct from repTargetUsed/setTargetUsed, which
  // are single set-level values sampled at completion.
  const [repRestTargets, setRepRestTargets] = useState([]);
  const lastRepStopRef = useRef(null); // epoch ms of the most recent rep end

  const restIntervalRef = useRef(null);

  // Rest count-up state — restElapsed lives in RestClocksDisplay; parent reads
  // via restElapsedRef for the freeze-capture in startStopwatch (display-only, never saved).
  const [restMode, setRestMode] = useState(null); // null | 'rep' | 'set'
  const [repRestFrozen, setRepRestFrozen] = useState(null);
  const [setRestFrozen, setSetRestFrozen] = useState(null);
  const [repTarget, setRepTarget] = useState(20);
  const [setTarget, setSetTarget] = useState(60);
  const [adjMode, setAdjMode] = useState('rep'); // which target +15/−15 currently adjusts
  const restElapsedRef = useRef(0); // kept current by RestClocksDisplay each tick
  const loadedTargetIndexRef = useRef(null); // set index whose restSeconds was loaded into repTarget
  const wakeLockRef = useRef(null);
  const persistRef = useRef({});
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [justResumed, setJustResumed] = useState(false);
  const [savedWorkoutId, setSavedWorkoutId] = useState(null);
  const [savingProgress, setSavingProgress] = useState(false);
  // One toast, many messages: 'Saved ✓', 'Tap ignored — under 5s'. Null = hidden.
  const [toast, setToast] = useState(null);

  // Persist pool prefs
  useEffect(() => {
    try { localStorage.setItem('dh_poolLength', poolLength); } catch (e) { console.warn('localStorage write failed:', e); }
  }, [poolLength]);
  useEffect(() => {
    try { localStorage.setItem('dh_poolUnit', poolUnit); } catch (e) { console.warn('localStorage write failed:', e); }
  }, [poolUnit]);
  useEffect(() => {
    try { localStorage.setItem('dh_displayUnit', displayUnit); } catch (e) { console.warn('localStorage write failed:', e); }
  }, [displayUnit]);

  const restoreSession = useCallback((saved) => {
    setWorkout(saved.workout);
    setCurrentIndex(saved.currentIndex);
    setCompletedSets(saved.completedSets);
    setCurrentRep(saved.currentRep);
    setRepTimes(saved.repTimes);
    setRepRests(saved.repRests ?? []);
    setRepRestTargets(saved.repRestTargets ?? []);
    lastRepStopRef.current = saved.lastRepStop ?? null;
    setWorkoutStartClock(saved.workoutStartClock);
    setStopwatchRunning(saved.stopwatchRunning ?? false);
    setStopwatchStartTime(saved.stopwatchStartTime ?? null);
    setSavedWorkoutId(saved.savedWorkoutId ?? null);
    // Restored targets are the user's live values (incl. any ±15 overrides) and must
    // win over the incoming set's prescribed rest — claim the index so the per-set
    // target loader treats this set as already loaded.
    setRepTarget(saved.repTarget ?? 20);
    setSetTarget(saved.setTarget ?? 60);
    loadedTargetIndexRef.current = saved.currentIndex;
    setJustResumed(true);
  }, []);

  // Check for a saved in-progress workout on mount
  useEffect(() => {
    const autoResume = localStorage.getItem('dh_swimAutoResume');
    localStorage.removeItem('dh_swimAutoResume'); // always clear first — never carry over to a future session
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (Date.now() - saved.savedAt < RESUME_TTL_MS) {
        if (autoResume) {
          localStorage.removeItem(PERSIST_KEY);
          restoreSession(saved);
        } else {
          setResumeCandidate(saved);
        }
      } else {
        localStorage.removeItem(PERSIST_KEY);
      }
    } catch {
      localStorage.removeItem(PERSIST_KEY);
    }
  }, [restoreSession]);

  useEffect(() => {
    if (!justResumed) return;
    const id = setTimeout(() => setJustResumed(false), 8000);
    return () => clearTimeout(id);
  }, [justResumed]);

  // Acquire wake lock when workout is active; release on workout end or unmount
  useEffect(() => {
    if (!workout) return;
    if (navigator.wakeLock) {
      navigator.wakeLock.request('screen')
        .then(s => { wakeLockRef.current = s; })
        .catch(() => {});
    }
    return () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [workout]);

  // Re-acquire wake lock on visibility return (locks auto-drop when tab backgrounds)
  useEffect(() => {
    if (!workout) return;
    const reacquire = () => {
      if (document.visibilityState === 'visible' && navigator.wakeLock) {
        navigator.wakeLock.request('screen')
          .then(s => { wakeLockRef.current = s; })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', reacquire);
    return () => document.removeEventListener('visibilitychange', reacquire);
  }, [workout]);

  // Persist workout state to localStorage on every relevant state change
  useEffect(() => {
    if (!workout) return;
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({
        workout, currentIndex, completedSets,
        currentRep, repTimes, workoutStartClock,
        stopwatchRunning, stopwatchStartTime,
        savedWorkoutId,
        repRests, repRestTargets, lastRepStop: lastRepStopRef.current,
        repTarget, setTarget,
        savedAt: Date.now(),
      }));
    } catch (e) {
      console.warn('Swim persist failed:', e);
    }
  }, [workout, currentIndex, completedSets, currentRep, repTimes, workoutStartClock, stopwatchRunning, stopwatchStartTime, savedWorkoutId, repRests, repRestTargets, repTarget, setTarget]);

  // Flush to localStorage on pagehide/visibilitychange; uses ref to avoid stale closures
  useEffect(() => {
    const flush = () => {
      const s = persistRef.current;
      if (!s.workout) return;
      try {
        localStorage.setItem(PERSIST_KEY, JSON.stringify({ ...s, savedAt: Date.now() }));
      } catch (e) {
        console.warn('Swim flush failed:', e);
      }
    };
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  // Per-set REP target: when a new set becomes current, adopt its prescribed restSeconds.
  // Blank / 0 / NaN leaves the current target alone (sticky carry-forward). Guarded by
  // index so re-renders from an unrelated workout change (e.g. inserting an unplanned
  // set) can't clobber a mid-set ±15 override — manual adjustment wins until the next set.
  // Never resets the ref on a null workout: this effect runs after the mount-resume
  // effect in the same commit, so clearing here would wipe the index restoreSession
  // just claimed and the prescribed rest would clobber the restored target.
  // startWorkout owns the reset.
  useEffect(() => {
    if (!workout) return;
    if (loadedTargetIndexRef.current === currentIndex) return;
    loadedTargetIndexRef.current = currentIndex;
    const prescribed = Number(workout.sets[currentIndex]?.restSeconds);
    if (prescribed > 0) setRepTarget(prescribed);
  }, [workout, currentIndex]);

  // Clear rest countdown on unmount — stopwatch and rest count-up are owned by sub-components
  useEffect(() => {
    return () => {
      if (restIntervalRef.current) clearInterval(restIntervalRef.current);
    };
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, TEMPLATES_LIST);
      setTemplates(
        items.map(it => ({
          id: it.id,
          name: it.Title || '',
          sets: safeParse(it.Sets),
          totalDistance: it.TotalDistance || 0,
        }))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTemplates(false);
    }
  }, [getToken]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(null), 2000);
  }

  // lastRepStopRef is intentionally NOT cleared here — it must survive the set
  // boundary so the next set's first rep records the inter-set rest.
  function resetStopwatch() {
    setCurrentRep(1);
    setRepTimes([]);
    setRepRests([]);
    setRepRestTargets([]);
    setStopwatchRunning(false);
    setStopwatchStartTime(null);
    setFrozenStopwatchTime(null);
  }

  function startStopwatch() {
    setJustResumed(false);
    if (restMode === 'rep') {
      setRepRestFrozen(restElapsedRef.current);
      clearRestCountUp();
    } else if (restMode === 'set') {
      setSetRestFrozen(restElapsedRef.current);
      setRepRestFrozen(null);
      clearRestCountUp();
    }
    clearRestTimer(); // Fix 2: kill any live rest countdown before the rep starts
    const now = Date.now();
    // Actual rest taken before this rep. Recorded raw — a long gap (Save Progress and
    // return, a phone call) is real data; filtering belongs downstream, not here.
    // restMode here is still the mode of the rest that just ended: clearRestCountUp
    // above only queues a state update, so the closure value is the pre-clear one.
    const hadPriorStop = lastRepStopRef.current != null;
    setRepRests(prev => [...prev, hadPriorStop
      ? Math.round(((now - lastRepStopRef.current) / 1000) * 10) / 10
      : null]);
    setRepRestTargets(prev => [...prev, hadPriorStop
      ? (restMode === 'set' ? setTarget : repTarget)
      : null]);
    setStopwatchStartTime(now);
    setStopwatchRunning(true);
    setFrozenStopwatchTime(null);
    if (!workoutStartClock) {
      setWorkoutStartClock(new Date().toISOString());
    }
  }

  function stopStopwatch() {
    if (!stopwatchStartTime) return;
    const elapsed = (Date.now() - stopwatchStartTime) / 1000;
    const roundedTime = Math.round(elapsed * 10) / 10;
    // Everything below this line is the commit — rep recorded, rest clock
    // started, rep counter advanced. A rejected tap must leave ALL of it
    // untouched: the stopwatch keeps running and the rep keeps timing, because
    // the tap was the accident, not the swim.
    if (elapsed < MIN_REP_SECONDS) {
      showToast(`Tap ignored — under ${MIN_REP_SECONDS}s`);
      return;
    }
    lastRepStopRef.current = Date.now(); // this rep ends here — the next rest measures from this instant
    setStopwatchRunning(false);
    setFrozenStopwatchTime(roundedTime);
    setRepTimes(prev => [...prev, roundedTime]);

    const set = currentSet();
    const totalReps = Number(set?.reps) || 1;
    const wasFinalRep = currentRep >= totalReps;
    if (!wasFinalRep) {
      setCurrentRep(prev => prev + 1);
    }
    // The break after a set's last rep is a SET rest, not a rep rest. Judging it
    // against the rep target made the clock flash red at every set boundary.
    startRestCountUp(wasFinalRep ? 'set' : 'rep');
  }

  function startWorkout(tmpl) {
    setWorkout({ templateName: tmpl.name, sets: [...tmpl.sets], startTime: Date.now() });
    setCurrentIndex(0);
    setCompletedSets([]);
    setShowSummary(false);
    setRestTimer(null);
    lastRepStopRef.current = null;      // no prior rep — first rest of the workout is null
    loadedTargetIndexRef.current = null; // let set 0's prescribed rest load
    resetStopwatch();
    setWorkoutStartClock(null);
    clearRestCountUp();
    setRepRestFrozen(null);
    setSetRestFrozen(null);
    setAdjMode('rep');
  }

  function convertDist(meters) {
    if (displayUnit === 'yd') return Math.round(meters / YARDS_TO_METERS);
    return Math.round(meters);
  }

  function toMeters(distance) {
    if (poolUnit === 'yd') return distance * YARDS_TO_METERS;
    return distance;
  }

  function currentSet() {
    if (!workout || currentIndex >= workout.sets.length) return null;
    return workout.sets[currentIndex];
  }

  function nextSet() {
    if (!workout || currentIndex + 1 >= workout.sets.length) return null;
    return workout.sets[currentIndex + 1];
  }

  function handleDoneSet() {
    setJustResumed(false);
    const set = editingCurrent || currentSet();
    if (!set) return;

    // Stop running stopwatch if active; capture time via Date.now() (sub-component owns the interval)
    let finalRepTimes = [...repTimes];
    if (stopwatchRunning && stopwatchStartTime) {
      const elapsed = Math.round(((Date.now() - stopwatchStartTime) / 1000) * 10) / 10;
      finalRepTimes = [...finalRepTimes, elapsed];
      // Done tapped mid-rep: that rep ends now. Without this stamp the next set's
      // first rest would wrongly include this rep's swim time.
      lastRepStopRef.current = Date.now();
    }

    const distMeters = toMeters((Number(set.distance) || 0) * (Number(set.reps) || 1));
    const bestTime = finalRepTimes.length > 0 ? Math.min(...finalRepTimes) : (set.timeSeconds || null);
    setCompletedSets(prev => [...prev, {
      ...set,
      actualDistance: set.distance,
      actualReps: set.reps,
      actualStroke: set.stroke,
      distanceMeters: distMeters,
      timeSeconds: bestTime,
      repTimes: finalRepTimes.length > 0 ? finalRepTimes : undefined,
      repRests: repRests.length > 0 ? repRests : undefined,
      repRestTargets: repRestTargets.length > 0 ? repRestTargets : undefined,
      repTargetUsed: repTarget,
      setTargetUsed: setTarget,
    }]);
    setEditingCurrent(null);
    resetStopwatch();

    const nextIdx = currentIndex + 1;
    if (nextIdx >= workout.sets.length) {
      clearRestTimer();
      clearRestCountUp();
      setShowSummary(true);
      return;
    }

    if (restMode === 'rep') setRepRestFrozen(restElapsedRef.current);
    // Idempotent: after the final rep the SET clock is already running and has been
    // accumulating since that rep's STOP. Restarting it here would zero
    // restElapsedRef and throw away the break the user already took. Only open a
    // fresh set count-up when we aren't already in one.
    if (restMode !== 'set') {
      startRestCountUp('set');
    } else {
      setAdjMode('set'); // clock keeps running; just make sure ±15 aims at the set target
    }

    // Start rest timer if rest is specified
    const rest = Number(set.restSeconds);
    if (rest > 0) {
      startRestTimer(rest, nextIdx);
    } else {
      setCurrentIndex(nextIdx);
    }
  }

  function startRestTimer(seconds, nextIdx) {
    clearRestTimer();
    setRestTimer(seconds);
    restIntervalRef.current = setInterval(() => {
      setRestTimer(prev => {
        if (prev <= 1) {
          clearInterval(restIntervalRef.current);
          restIntervalRef.current = null;
          setCurrentIndex(nextIdx);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function clearRestTimer() {
    if (restIntervalRef.current) {
      clearInterval(restIntervalRef.current);
      restIntervalRef.current = null;
    }
    setRestTimer(null);
  }

  // Stopping rest count-up: set restMode to null — RestClocksDisplay's useEffect
  // cleanup fires automatically when the dep changes, clearing its internal interval.
  function clearRestCountUp() {
    setRestMode(null);
  }

  // Starting rest count-up: reset the elapsed ref and set mode — RestClocksDisplay
  // watches restMode and starts a fresh interval with a new `now` on each transition.
  function startRestCountUp(mode) {
    restElapsedRef.current = 0;
    setRestMode(mode);
    setAdjMode(mode);
  }

  function skipRest() {
    const nextIdx = currentIndex + 1;
    clearRestTimer();
    setCurrentIndex(nextIdx);
  }

  function handleEditCurrent() {
    const set = currentSet();
    setEditingCurrent({ ...set });
  }

  function handleAddUnplanned() {
    setNewSet({ distance: 100, reps: 1, stroke: 'Free', notes: '', restSeconds: '', sectionHeader: '', timeSeconds: '' });
    setShowAddSet(true);
  }

  function confirmAddSet() {
    if (!newSet) return;
    // Insert after current index
    setWorkout(prev => {
      const sets = [...prev.sets];
      sets.splice(currentIndex + 1, 0, { ...newSet, unplanned: true });
      return { ...prev, sets };
    });
    setShowAddSet(false);
    setNewSet(null);
  }

  function calcTotalDistance(sets = completedSets) {
    return sets.reduce((sum, s) => sum + (s.distanceMeters || 0), 0);
  }

  // Pure snapshot for saving: completedSets plus the in-progress partial set, if any.
  // Mirrors handleDoneSet's math but never touches state — the live workout continues unchanged.
  function buildEffectiveCompletedSets() {
    const set = editingCurrent || currentSet();
    const hasPartial = set && (repTimes.length > 0 || (stopwatchRunning && stopwatchStartTime));
    if (!hasPartial) return completedSets;

    let finalRepTimes = [...repTimes];
    if (stopwatchRunning && stopwatchStartTime) {
      const elapsed = Math.round(((Date.now() - stopwatchStartTime) / 1000) * 10) / 10;
      finalRepTimes = [...finalRepTimes, elapsed];
    }

    const actualRepsDone = finalRepTimes.length;
    return [...completedSets, {
      ...set,
      actualDistance: set.distance,
      actualReps: actualRepsDone,
      actualStroke: set.stroke,
      distanceMeters: toMeters((Number(set.distance) || 0) * actualRepsDone),
      timeSeconds: Math.min(...finalRepTimes),
      repTimes: finalRepTimes,
      repRests: repRests.length > 0 ? repRests : undefined,
      repRestTargets: repRestTargets.length > 0 ? repRestTargets : undefined,
      repTargetUsed: repTarget,
      setTargetUsed: setTarget,
      partial: true,
    }];
  }

  async function saveProgress() {
    const effectiveSets = buildEffectiveCompletedSets();
    if (effectiveSets.length === 0) return;
    setSavingProgress(true);
    try {
      const token = await getToken();
      const totalMeters = calcTotalDistance(effectiveSets);
      const endTime = new Date().toISOString();
      const durationSec = workoutStartClock
        ? Math.round((new Date(endTime) - new Date(workoutStartClock)) / 1000)
        : Math.round((Date.now() - workout.startTime) / 1000);
      const fields = {
        Title: `${workout.templateName} - ${new Date().toLocaleDateString()}`,
        TemplateName: workout.templateName,
        Sets: JSON.stringify(effectiveSets),
        TotalDistanceMeters: Math.round(totalMeters),
        PoolLength: poolLength,
        PoolUnit: poolUnit,
        DurationSeconds: durationSec,
        WorkoutDate: new Date().toISOString(),
        CreatedByEmail: userEmail,
        WorkoutStartTime: workoutStartClock || '',
        WorkoutEndTime: endTime,
      };
      if (savedWorkoutId) {
        await updateItem(token, WORKOUTS_LIST, savedWorkoutId, fields);
      } else {
        const created = await createItem(token, WORKOUTS_LIST, fields);
        setSavedWorkoutId(created.id);
      }
      showToast('Saved ✓');
    } catch (e) {
      console.warn('Save progress failed:', e);
    } finally {
      setSavingProgress(false);
    }
  }

  async function saveWorkout() {
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const effectiveSets = buildEffectiveCompletedSets();
      const totalMeters = calcTotalDistance(effectiveSets);
      const endTime = new Date().toISOString();
      const durationSec = workoutStartClock
        ? Math.round((new Date(endTime) - new Date(workoutStartClock)) / 1000)
        : Math.round((Date.now() - workout.startTime) / 1000);
      const fields = {
        Title: `${workout.templateName} - ${new Date().toLocaleDateString()}`,
        TemplateName: workout.templateName,
        Sets: JSON.stringify(effectiveSets),
        TotalDistanceMeters: Math.round(totalMeters),
        PoolLength: poolLength,
        PoolUnit: poolUnit,
        DurationSeconds: durationSec,
        WorkoutDate: new Date().toISOString(),
        CreatedByEmail: userEmail,
        WorkoutStartTime: workoutStartClock || '',
        WorkoutEndTime: endTime,
      };
      if (savedWorkoutId) {
        await updateItem(token, WORKOUTS_LIST, savedWorkoutId, fields);
      } else {
        await createItem(token, WORKOUTS_LIST, fields);
      }
      setWorkout(null);
      setShowSummary(false);
      setCompletedSets([]);
      setSavedWorkoutId(null);
      localStorage.removeItem(PERSIST_KEY);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveAndEnd() {
    const effectiveSets = buildEffectiveCompletedSets();
    if (effectiveSets.length === 0) {
      discardWorkout();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const totalMeters = calcTotalDistance(effectiveSets);
      const endTime = new Date().toISOString();
      const durationSec = workoutStartClock
        ? Math.round((new Date(endTime) - new Date(workoutStartClock)) / 1000)
        : Math.round((Date.now() - workout.startTime) / 1000);
      const fields = {
        Title: `${workout.templateName} - ${new Date().toLocaleDateString()}`,
        TemplateName: workout.templateName,
        Sets: JSON.stringify(effectiveSets),
        TotalDistanceMeters: Math.round(totalMeters),
        PoolLength: poolLength,
        PoolUnit: poolUnit,
        DurationSeconds: durationSec,
        WorkoutDate: new Date().toISOString(),
        CreatedByEmail: userEmail,
        WorkoutStartTime: workoutStartClock || '',
        WorkoutEndTime: endTime,
      };
      if (savedWorkoutId) {
        await updateItem(token, WORKOUTS_LIST, savedWorkoutId, fields);
      } else {
        await createItem(token, WORKOUTS_LIST, fields);
      }
      clearRestTimer();
      clearRestCountUp();
      setRepRestFrozen(null);
      setSetRestFrozen(null);
      setAdjMode('rep');
      resetStopwatch();
      setWorkout(null);
      setShowSummary(false);
      setCompletedSets([]);
      setEditingCurrent(null);
      setSavedWorkoutId(null);
      localStorage.removeItem(PERSIST_KEY);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function discardWorkout() {
    if (completedSets.length > 0 && !window.confirm("Are you sure? This workout won't be recorded.")) return;
    clearRestTimer();
    clearRestCountUp();
    setRepRestFrozen(null);
    setSetRestFrozen(null);
    setAdjMode('rep');
    resetStopwatch();
    setWorkout(null);
    setShowSummary(false);
    setCompletedSets([]);
    setEditingCurrent(null);
    setSavedWorkoutId(null);
    localStorage.removeItem(PERSIST_KEY);
  }

  function handleResume() {
    restoreSession(resumeCandidate);
    setResumeCandidate(null);
    localStorage.removeItem(PERSIST_KEY);
  }

  function handleDiscardResume() {
    localStorage.removeItem(PERSIST_KEY);
    setResumeCandidate(null);
  }

  function formatRest(sec) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  }

  // Keep persistRef current so the flush handler never reads stale state
  persistRef.current = { workout, currentIndex, completedSets, currentRep, repTimes, workoutStartClock, stopwatchRunning, stopwatchStartTime, savedWorkoutId, repRests, repRestTargets, lastRepStop: lastRepStopRef.current, repTarget, setTarget };

  // --- RENDER ---

  // Resume prompt — shown when a saved in-progress workout is found on mount
  if (resumeCandidate && !workout) {
    const minAgo = Math.round((Date.now() - resumeCandidate.savedAt) / 60000);
    const agoLabel = minAgo < 1 ? 'just now' : minAgo === 1 ? '1 minute ago' : `${minAgo} minutes ago`;
    return (
      <div className="SwimWorkout">
        <div className="SwimWorkout-resume-prompt">
          <h3 className="SwimWorkout-resume-heading">Workout In Progress</h3>
          <p className="SwimWorkout-resume-name">{resumeCandidate.workout.templateName}</p>
          <p className="SwimWorkout-resume-meta">
            Set {resumeCandidate.currentIndex + 1} of {resumeCandidate.workout.sets.length}
            &nbsp;&middot;&nbsp;
            {resumeCandidate.completedSets.length} set{resumeCandidate.completedSets.length !== 1 ? 's' : ''} completed
          </p>
          <p className="SwimWorkout-resume-time">Saved {agoLabel}</p>
          <div className="SwimWorkout-resume-actions">
            <button className="Fitness-btn" onClick={handleResume}>Resume</button>
            <button className="Fitness-btn Fitness-btn--secondary" onClick={handleDiscardResume}>Start Fresh</button>
          </div>
        </div>
      </div>
    );
  }

  // Workout summary
  if (showSummary && workout) {
    const totalMeters = calcTotalDistance();
    const elapsed = workoutStartClock
      ? Math.round((Date.now() - new Date(workoutStartClock).getTime()) / 1000)
      : Math.round((Date.now() - workout.startTime) / 1000);
    const elapsedMin = Math.floor(elapsed / 60);
    const elapsedSec = elapsed % 60;
    const startTimeDisplay = workoutStartClock
      ? new Date(workoutStartClock).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      : null;
    return (
      <div className="SwimWorkout-summary">
        <h2>Workout Complete</h2>
        {error && <p className="Fitness-error">{error}</p>}
        <div className="SwimWorkout-summary-stats">
          <div className="SwimWorkout-summary-stat">
            <span className="SwimWorkout-summary-label">Total Distance</span>
            <span className="SwimWorkout-summary-value">{convertDist(totalMeters)}{displayUnit}</span>
          </div>
          <div className="SwimWorkout-summary-stat">
            <span className="SwimWorkout-summary-label">Sets</span>
            <span className="SwimWorkout-summary-value">{completedSets.length}</span>
          </div>
          <div className="SwimWorkout-summary-stat">
            <span className="SwimWorkout-summary-label">Total Time</span>
            <span className="SwimWorkout-summary-value">{elapsedMin}:{String(elapsedSec).padStart(2, '0')}</span>
          </div>
          {startTimeDisplay && (
            <div className="SwimWorkout-summary-stat">
              <span className="SwimWorkout-summary-label">Started</span>
              <span className="SwimWorkout-summary-value">{startTimeDisplay}</span>
            </div>
          )}
        </div>

        <div className="SwimWorkout-summary-sets">
          {completedSets.map((s, i) => (
            <div key={i} className={`SwimWorkout-summary-set${s.unplanned ? ' SwimWorkout-summary-set--unplanned' : ''}`}>
              {s.sectionHeader && <div className="SwimWorkout-section-label">{s.sectionHeader}</div>}
              <span>{s.actualReps > 1 ? `${s.actualReps}x` : ''}{s.actualDistance} {s.actualStroke}</span>
              {s.notes && <span className="SwimWorkout-set-notes">{s.notes}</span>}
              {s.repTimes && s.repTimes.length > 0 && (
                <span className="SwimWorkout-set-time">
                  {s.repTimes.map((t, ri) => `R${ri + 1}: ${formatStopwatch(t)}`).join(' | ')}
                </span>
              )}
              {!s.repTimes && s.timeSeconds && <span className="SwimWorkout-set-time">{s.timeSeconds}s</span>}
              {s.unplanned && <span className="SwimWorkout-set-tag">added</span>}
            </div>
          ))}
        </div>

        <div className="SwimWorkout-summary-actions">
          <button className="Fitness-btn" onClick={saveWorkout} disabled={saving}>
            {saving ? 'Saving...' : 'Save Workout'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary" onClick={discardWorkout}>Discard</button>
        </div>
      </div>
    );
  }

  // Active workout - poolside mode
  if (workout && poolsideMode) {
    const set = currentSet();
    const next = nextSet();
    const progress = `${currentIndex + 1} / ${workout.sets.length}`;
    const runningDist = completedSets.reduce((sum, s) => sum + (s.distanceMeters || 0), 0);

    // Rest timer screen
    if (restTimer !== null) {
      return (
        <div className="SwimWorkout-poolside SwimWorkout-poolside--rest">
          <div className="SwimWorkout-poolside-header">
            <span>REST</span>
            <button className="Fitness-btn Fitness-btn--small" onClick={skipRest}>Skip</button>
          </div>
          <div className="SwimWorkout-poolside-timer">{formatRest(restTimer)}</div>
          {next && (
            <div className="SwimWorkout-poolside-next">
              <span className="SwimWorkout-poolside-next-label">Up Next:</span>
              {next.sectionHeader && <div className="SwimWorkout-poolside-section">{next.sectionHeader}</div>}
              <div className="SwimWorkout-poolside-next-set">
                {next.reps > 1 ? `${next.reps}x` : ''}{next.distance} {next.stroke}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Edit current set overlay
    if (editingCurrent) {
      return (
        <div className="SwimWorkout-poolside">
          <h3 className="SwimWorkout-poolside-edit-title">Edit Set Before Confirming</h3>
          <div className="SwimWorkout-poolside-edit-fields">
            <label>
              Reps
              <input className="Fitness-input Fitness-input--poolside" type="number" min="1"
                value={editingCurrent.reps} onChange={e => setEditingCurrent(p => ({ ...p, reps: parseInt(e.target.value) || 1 }))} />
            </label>
            <label>
              Distance
              <input className="Fitness-input Fitness-input--poolside" type="number" min="0" step="25"
                value={editingCurrent.distance} onChange={e => setEditingCurrent(p => ({ ...p, distance: parseInt(e.target.value) || 0 }))} />
            </label>
            <label>
              Stroke
              <select className="Fitness-select Fitness-select--poolside"
                value={editingCurrent.stroke} onChange={e => setEditingCurrent(p => ({ ...p, stroke: e.target.value }))}>
                {STROKES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>
              Time (sec)
              <input className="Fitness-input Fitness-input--poolside" type="number" min="0"
                value={editingCurrent.timeSeconds || ''} onChange={e => setEditingCurrent(p => ({ ...p, timeSeconds: e.target.value }))} />
            </label>
          </div>
          <div className="SwimWorkout-poolside-actions">
            <button className="Fitness-btn Fitness-btn--poolside" onClick={handleDoneSet}>Confirm</button>
            <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setEditingCurrent(null)}>Cancel</button>
          </div>
        </div>
      );
    }

    if (!set) return null;

    const totalReps = Number(set.reps) || 1;
    const allRepsTimed = repTimes.length >= totalReps;
    const showRepCounter = totalReps > 1;
    const repBadgeClass = `SwimWorkout-rep-badge${
      currentRep === totalReps ? ' SwimWorkout-rep-badge--red'
      : currentRep === totalReps - 1 ? ' SwimWorkout-rep-badge--amber'
      : ''
    }`;

    return (
      <div className="SwimWorkout-poolside">
        <div className="SwimWorkout-poolside-header">
          <span className="SwimWorkout-poolside-progress">{progress}</span>
          <span className="SwimWorkout-poolside-running">{convertDist(runningDist)}{displayUnit}</span>
          <button className="Fitness-btn Fitness-btn--small" onClick={() => setPoolsideMode(false)}>Normal View</button>
        </div>

        {justResumed && (
          <div
            className="SwimWorkout-poolside-resumed-banner"
            onClick={() => setJustResumed(false)}
          >
            RESUMED — Set {currentIndex + 1} of {workout.sets.length} · {completedSets.length} set{completedSets.length !== 1 ? 's' : ''} logged
          </div>
        )}

        {toast && (
          <div className="SwimWorkout-poolside-resumed-banner SwimWorkout-poolside-saved-toast">
            {toast}
          </div>
        )}

        {set.sectionHeader && <div className="SwimWorkout-poolside-section">{set.sectionHeader}</div>}

        <div className="SwimWorkout-poolside-main">
          <div className="SwimWorkout-poolside-reps-wrap">
            <img src={defineholyLogo} alt="Define Holy" className="SwimWorkout-poolside-logo" />
            <div className="SwimWorkout-poolside-reps">
              {set.reps > 1 ? `${set.reps}x` : ''}{set.distance}
            </div>
          </div>
          <div className="SwimWorkout-poolside-stroke">{set.stroke}</div>
          {set.notes && <div className="SwimWorkout-poolside-notes">{set.notes}</div>}
          {set.restSeconds && <div className="SwimWorkout-poolside-rest-info">Rest: {formatRest(Number(set.restSeconds))}</div>}

          {/* Rep counter */}
          {showRepCounter && (
            <div className="SwimWorkout-poolside-rep-counter">
              Rep {Math.min(currentRep, totalReps)} of {totalReps}
            </div>
          )}

          {/* Goggle-readable rep badge — absolute-floated right, frame flashes not digit */}
          {showRepCounter && (
            <div className={repBadgeClass}>
              <div className="SwimWorkout-rep-badge__digit">{Math.min(currentRep, totalReps)}</div>
              <div className="SwimWorkout-rep-badge__total">/{totalReps}</div>
            </div>
          )}

          {/* Stopwatch display — isolated sub-component, 100ms tick stays out of parent tree */}
          <StopwatchDisplay
            startTime={stopwatchStartTime}
            running={stopwatchRunning}
            frozenTime={frozenStopwatchTime}
          />

          {/* Rep times log */}
          {repTimes.length > 0 && (
            <div className="SwimWorkout-poolside-rep-times">
              {repTimes.map((t, i) => (
                <span key={i} className="SwimWorkout-poolside-rep-time">
                  R{i + 1}: {formatStopwatch(t)}
                </span>
              ))}
            </div>
          )}
        </div>

        {next && (
          <div className="SwimWorkout-poolside-next">
            <span className="SwimWorkout-poolside-next-label">Next:</span>
            <span>{next.reps > 1 ? `${next.reps}x` : ''}{next.distance} {next.stroke}</span>
          </div>
        )}

        {/* Rest clocks — isolated sub-component, 100ms tick stays out of parent tree */}
        <RestClocksDisplay
          restMode={restMode}
          frozenRepRest={repRestFrozen}
          frozenSetRest={setRestFrozen}
          repTarget={repTarget}
          setTarget={setTarget}
          adjMode={adjMode}
          setRepTarget={setRepTarget}
          setSetTarget={setSetTarget}
          setAdjMode={setAdjMode}
          elapsedRef={restElapsedRef}
        />

        {/* Stopwatch buttons */}
        <div className="SwimWorkout-poolside-stopwatch-actions">
          {!stopwatchRunning && !allRepsTimed && (
            <button className="Fitness-btn Fitness-btn--poolside SwimWorkout-stopwatch-start" onClick={startStopwatch}>
              START
            </button>
          )}
          {stopwatchRunning && (
            <button className="Fitness-btn Fitness-btn--poolside SwimWorkout-stopwatch-stop" onClick={stopStopwatch}>
              STOP
            </button>
          )}
        </div>

        <div className="SwimWorkout-poolside-actions">
          <button className="Fitness-btn Fitness-btn--poolside Fitness-btn--done" onClick={handleDoneSet}>Done</button>
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--poolside-sm" onClick={handleEditCurrent}>Edit</button>
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--poolside-sm" onClick={handleAddUnplanned}>+ Set</button>
        </div>

        <div className="SwimWorkout-poolside-end-actions">
          <button
            className="Fitness-btn Fitness-btn--secondary Fitness-btn--poolside-sm SwimWorkout-poolside-progress-save"
            onClick={saveProgress}
            disabled={savingProgress || saving || (completedSets.length === 0 && repTimes.length === 0 && !stopwatchRunning)}
          >
            {savingProgress ? 'Saving...' : 'Save Progress'}
          </button>
          <button className="Fitness-btn Fitness-btn--poolside-sm SwimWorkout-poolside-save" onClick={saveAndEnd} disabled={saving || savingProgress}>
            {saving ? 'Saving...' : 'Save & End'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--small SwimWorkout-poolside-discard" onClick={discardWorkout}>
            Discard
          </button>
        </div>

        {showAddSet && newSet && (
          <div className="SwimWorkout-add-overlay">
            <h3>Add Unplanned Set</h3>
            <div className="SwimWorkout-add-fields">
              <label>Reps <input className="Fitness-input" type="number" min="1" value={newSet.reps}
                onChange={e => setNewSet(p => ({ ...p, reps: parseInt(e.target.value) || 1 }))} /></label>
              <label>Distance <input className="Fitness-input" type="number" min="0" step="25" value={newSet.distance}
                onChange={e => setNewSet(p => ({ ...p, distance: parseInt(e.target.value) || 0 }))} /></label>
              <label>Stroke <select className="Fitness-select" value={newSet.stroke}
                onChange={e => setNewSet(p => ({ ...p, stroke: e.target.value }))}>
                {STROKES.map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
              <label>Notes <input className="Fitness-input" type="text" value={newSet.notes}
                onChange={e => setNewSet(p => ({ ...p, notes: e.target.value }))} /></label>
            </div>
            <div className="SwimWorkout-add-actions">
              <button className="Fitness-btn" onClick={confirmAddSet}>Add</button>
              <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setShowAddSet(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Active workout - normal (non-poolside) view
  if (workout) {
    const set = currentSet();
    const runningDist = completedSets.reduce((sum, s) => sum + (s.distanceMeters || 0), 0);

    if (restTimer !== null) {
      return (
        <div className="SwimWorkout">
          <div className="SwimWorkout-rest-banner">
            <span>Rest: {formatRest(restTimer)}</span>
            <button className="Fitness-btn Fitness-btn--small" onClick={skipRest}>Skip</button>
          </div>
          <button className="Fitness-btn Fitness-btn--small" onClick={() => setPoolsideMode(true)}>Poolside Mode</button>
        </div>
      );
    }

    return (
      <div className="SwimWorkout">
        <div className="SwimWorkout-header">
          <h2>{workout.templateName}</h2>
          <button className="Fitness-btn Fitness-btn--small" onClick={() => setPoolsideMode(true)}>Poolside Mode</button>
        </div>
        <div className="SwimWorkout-progress-bar">
          <div className="SwimWorkout-progress-fill" style={{ width: `${(currentIndex / workout.sets.length) * 100}%` }} />
        </div>
        <p>Set {currentIndex + 1} of {workout.sets.length} | {convertDist(runningDist)}{displayUnit} so far</p>

        {toast && <p className="SwimWorkout-saved-toast">{toast}</p>}
        {error && <p className="Fitness-error">{error}</p>}

        {set && (
          <div className="SwimWorkout-current">
            {set.sectionHeader && <div className="SwimWorkout-section-label">{set.sectionHeader}</div>}
            <div className="SwimWorkout-current-detail">
              <strong>{set.reps > 1 ? `${set.reps}x` : ''}{set.distance} {set.stroke}</strong>
              {set.notes && <span className="SwimWorkout-set-notes">{set.notes}</span>}
              {set.restSeconds && <span>Rest: {formatRest(Number(set.restSeconds))}</span>}
            </div>
            <div className="SwimWorkout-current-actions">
              <button className="Fitness-btn" onClick={handleDoneSet}>Done</button>
              <button className="Fitness-btn Fitness-btn--secondary" onClick={handleEditCurrent}>Edit</button>
              <button className="Fitness-btn Fitness-btn--secondary" onClick={handleAddUnplanned}>+ Add Set</button>
            </div>
          </div>
        )}

        {completedSets.length > 0 && (
          <div className="SwimWorkout-completed-list">
            <h4>Completed</h4>
            {completedSets.map((s, i) => (
              <div key={i} className="SwimWorkout-completed-item">
                {s.actualReps > 1 ? `${s.actualReps}x` : ''}{s.actualDistance} {s.actualStroke}
                {s.notes ? ` (${s.notes})` : ''}
                {s.unplanned ? ' [added]' : ''}
              </div>
            ))}
          </div>
        )}

        <div className="SwimWorkout-end-actions">
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--small" onClick={saveProgress} disabled={savingProgress || saving || (completedSets.length === 0 && repTimes.length === 0 && !stopwatchRunning)}>
            {savingProgress ? 'Saving...' : 'Save Progress'}
          </button>
          <button className="Fitness-btn Fitness-btn--small" onClick={saveAndEnd} disabled={saving || savingProgress}>
            {saving ? 'Saving...' : 'Save & End'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary Fitness-btn--small" onClick={discardWorkout}>Discard</button>
        </div>

        {showAddSet && newSet && (
          <div className="SwimWorkout-add-overlay">
            <h3>Add Unplanned Set</h3>
            <div className="SwimWorkout-add-fields">
              <label>Reps <input className="Fitness-input" type="number" min="1" value={newSet.reps}
                onChange={e => setNewSet(p => ({ ...p, reps: parseInt(e.target.value) || 1 }))} /></label>
              <label>Distance <input className="Fitness-input" type="number" min="0" step="25" value={newSet.distance}
                onChange={e => setNewSet(p => ({ ...p, distance: parseInt(e.target.value) || 0 }))} /></label>
              <label>Stroke <select className="Fitness-select" value={newSet.stroke}
                onChange={e => setNewSet(p => ({ ...p, stroke: e.target.value }))}>
                {STROKES.map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
              <label>Notes <input className="Fitness-input" type="text" value={newSet.notes}
                onChange={e => setNewSet(p => ({ ...p, notes: e.target.value }))} /></label>
            </div>
            <div className="SwimWorkout-add-actions">
              <button className="Fitness-btn" onClick={confirmAddSet}>Add</button>
              <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setShowAddSet(false)}>Cancel</button>
            </div>
          </div>
        )}

        {editingCurrent && (
          <div className="SwimWorkout-add-overlay">
            <h3>Edit Current Set</h3>
            <div className="SwimWorkout-add-fields">
              <label>Reps <input className="Fitness-input" type="number" min="1" value={editingCurrent.reps}
                onChange={e => setEditingCurrent(p => ({ ...p, reps: parseInt(e.target.value) || 1 }))} /></label>
              <label>Distance <input className="Fitness-input" type="number" min="0" step="25" value={editingCurrent.distance}
                onChange={e => setEditingCurrent(p => ({ ...p, distance: parseInt(e.target.value) || 0 }))} /></label>
              <label>Stroke <select className="Fitness-select" value={editingCurrent.stroke}
                onChange={e => setEditingCurrent(p => ({ ...p, stroke: e.target.value }))}>
                {STROKES.map(s => <option key={s} value={s}>{s}</option>)}
              </select></label>
              <label>Time (sec) <input className="Fitness-input" type="number" min="0" value={editingCurrent.timeSeconds || ''}
                onChange={e => setEditingCurrent(p => ({ ...p, timeSeconds: e.target.value }))} /></label>
            </div>
            <div className="SwimWorkout-add-actions">
              <button className="Fitness-btn" onClick={handleDoneSet}>Confirm</button>
              <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setEditingCurrent(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Template selection / pre-workout screen
  return (
    <div className="SwimWorkout">
      <div className="SwimWorkout-settings">
        <h3>Pool Settings</h3>
        <div className="SwimWorkout-pool-row">
          <label>
            Pool Length:
            <input className="Fitness-input Fitness-input--tiny" type="number" min="1"
              value={poolLength} onChange={e => setPoolLength(Number(e.target.value) || 25)} />
          </label>
          <div className="SwimWorkout-pool-toggles">
            <button className={`Fitness-btn Fitness-btn--small${poolUnit === 'm' ? ' Fitness-btn--active' : ''}`}
              onClick={() => { setPoolUnit('m'); setPoolLength(25); }}>25m</button>
            <button className={`Fitness-btn Fitness-btn--small${poolUnit === 'yd' ? ' Fitness-btn--active' : ''}`}
              onClick={() => { setPoolUnit('yd'); setPoolLength(25); }}>25yd</button>
          </div>
        </div>
        <div className="SwimWorkout-display-row">
          <span>Display distances in:</span>
          <button className={`Fitness-btn Fitness-btn--small${displayUnit === 'm' ? ' Fitness-btn--active' : ''}`}
            onClick={() => setDisplayUnit('m')}>Meters</button>
          <button className={`Fitness-btn Fitness-btn--small${displayUnit === 'yd' ? ' Fitness-btn--active' : ''}`}
            onClick={() => setDisplayUnit('yd')}>Yards</button>
        </div>
      </div>

      <h3>Select a Template</h3>
      {error && <p className="Fitness-error">{error}</p>}
      {loadingTemplates && <p className="Fitness-loading">Loading templates...</p>}
      {!loadingTemplates && templates.length === 0 && (
        <p className="Fitness-empty">No templates yet. Go to Templates tab to create one.</p>
      )}
      <div className="SwimWorkout-template-list">
        {templates.map(tmpl => (
          <div key={tmpl.id} className="SwimWorkout-template-card" onClick={() => startWorkout(tmpl)}>
            <h4>{tmpl.name}</h4>
            <span>{tmpl.totalDistance}m &middot; {tmpl.sets.length} sets</span>
          </div>
        ))}
      </div>
    </div>
  );
}

class SwimErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, remountKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.warn('SwimmingWorkout crashed, session preserved in localStorage:', error);
  }

  handleResume() {
    try { localStorage.setItem('dh_swimAutoResume', '1'); } catch (e) { console.warn('localStorage write failed:', e); }
    this.setState(s => ({ hasError: false, remountKey: s.remountKey + 1 }));
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="SwimWorkout">
          <div className="SwimWorkout-resume-prompt">
            <h3 className="SwimWorkout-resume-heading">Workout interrupted</h3>
            <p className="SwimWorkout-resume-meta">Your progress is saved.</p>
            <div className="SwimWorkout-resume-actions">
              <button className="Fitness-btn" onClick={() => this.handleResume()}>Resume Workout</button>
            </div>
          </div>
        </div>
      );
    }
    return <div key={this.state.remountKey}>{this.props.children}</div>;
  }
}

function SwimmingWorkoutWithBoundary(props) {
  return <SwimErrorBoundary><SwimmingWorkout {...props} /></SwimErrorBoundary>;
}

export default SwimmingWorkoutWithBoundary;
